import '../../base/outline-container/outline-container';
import { CSSResultGroup, TemplateResult, html } from 'lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { customElement, property, state } from 'lit/decorators.js';
import componentStyles from './idb-section-wrapper.css.lit';
import { ClassInfo, classMap } from 'lit/directives/class-map.js';
import { SlotController } from '../../controllers/slot-controller';
import { backgroundPropertyData } from '../../../data';
/**
 * Define the available background colors and an
 * associated type definition.
 */

export type SectionBackgrounds = typeof backgroundPropertyData;
export type SectionExtraClasses =
  | 'default'
  | 'less-space'
  | 'less-top-space'
  | 'less-bottom-space'
  | 'y-padding-md'
  | 'y-padding-lg'
  | 'y-padding-xl'
  | 'no-padding'
  | 'description-narrower';

export type ctaTopMargins = 'none' | 'sm' | 'md' | 'lg';

export type HeaderAlignments = 'left' | 'center' | 'right';

/**
 * The IDB Section component
 * @element idb-section-wrapper
 * @slot eyebrow - The eyebrow slot.
 * @slot title - The title slot.
 * @slot subtitle - The subtitle slot.
 * @slot description - The description slot.
 * @slot content - The primary content slot.
 * @slot cta - The CTA slot.
 * @see https://yoksel.github.io/url-encoder/ to encode wave backgrounds
 * @todo update the slots to template partials.
 */
@customElement('idb-section-wrapper')
export class IdbSection extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  slots = new SlotController(
    this, // This, the host element.
    false // To shift or not to shift LightDom nodes to ShadowDOM.
  );
  /**
   * The background of the section.
   */
  @property({ type: String, attribute: 'section-background' })
  sectionBackground: SectionBackgrounds;

  /**
   * The extra classes for the section.
   */
  @property({ type: String, attribute: 'extra-classes' })
  extraClasses: SectionExtraClasses = 'default';

  @property({ type: String, attribute: 'cta-top-margin' })
  ctaTopMargin: ctaTopMargins = 'none';

  @property({ type: String, attribute: 'header-alignment' })
  headerAlignment: HeaderAlignments = 'center';

  @property({ type: String, attribute: 'eyebrow' })
  eyebrow: string;

  @property({ type: String, attribute: 'heading' })
  heading: string;

  @property({ type: String, attribute: 'subheading' })
  subheading: string;

  @property({ type: Boolean, reflect: true, attribute: 'remove-x-padding'})
  removeXPadding = false;

  @state() hasDescriptionSlot: boolean;
  @state() hasContentSlot: boolean;
  @state() hasCtaSlot: boolean;

  connectedCallback() {
    super.connectedCallback();
    this.hasDescriptionSlot = this.slots.test('description');
    this.hasContentSlot = this.slots.test('content');
    this.hasCtaSlot = this.slots.test('cta');
  }

  firstUpdated(): void {}

  render(): TemplateResult {
    const sectionClasses = {
      'idb-section-wrapper': true,
      [`section-background--${this.sectionBackground}`]:
        this.sectionBackground !== undefined,
      [`extra-classes--${this.extraClasses}`]: this.extraClasses !== undefined,
    };
    const sectionHeaderClasses = {
      'section-header': true,
      [`section-header--alignment-${this.headerAlignment}`]:
        this.headerAlignment !== undefined,
    };
    const sectionContentClasses = {
      'section-content': true,
    };

    return html`
      <section class="${classMap(sectionClasses)}">  
        ${this.sectionHeaderTemplate(sectionHeaderClasses)}
        ${this.sectionContentTemplate(sectionContentClasses)}
      </section>
    `;
  }

  /**
   * Template partial for the Content slot.
   * @param classes
   * @returns TemplateResult | null
   */
  sectionHeaderTemplate(classes: ClassInfo): TemplateResult | null {
    return html` <section class="${classMap(classes)}">
      <outline-container x-padding>
      ${this.eyebrowTemplate()}
      ${this.titleTemplate()}
      ${this.subtitleTemplate()}
      ${
        this.hasDescriptionSlot
          ? html`
      <div class="description-wrapper">
        <slot name="description"></slot>
      </div>`
          : null
      }
      </outline-container>
    </section>`;
  }

  /**
   * Template partial for the Eyebrow slot.
   * @returns TemplateResult | null
   */
  eyebrowTemplate(): TemplateResult | null {
    if (!this.eyebrow) return null;
    return html`<idb-heading level="p" level-size="sm" level-style="semibold" capital-style="uppercase">
      ${this.eyebrow}
    </idb-heading>`;
  }

  /**
   * Template partial for the Title slot.
   * @returns TemplateResult | null
   */
  titleTemplate(): TemplateResult | null {
    if (!this.heading) return null;
    return html`
    <idb-heading level-size="4xl" level-style="semibold">
      ${this.heading}
    </idb-heading>`;
  }

  /**
   * Template partial for the Title slot.
   * @returns TemplateResult | null
   */
  subtitleTemplate(): TemplateResult | null {
    if (!this.subheading) return null;
    return html`
    <idb-heading accent-color="" level="h3" level-style="semibold">
      ${this.subheading}
    </idb-heading>`;
  }

  /**
   * Template partial for the Content slot.
   * @param classes
   * @returns TemplateResult | null
   */
  sectionContentTemplate(classes: ClassInfo): TemplateResult | null {
    if (!this.hasContentSlot && !this.hasCtaSlot) return null;

    return html` <section class="${classMap(classes)}">
      ${this.contentTemplate()}
      ${this.ctaTemplate()}
    </section>`;
  }

  /**
   * Template partial for the Content slot.
   * @param classes
   * @returns TemplateResult | null
   */
  contentTemplate(): TemplateResult | null {
    if (!this.hasContentSlot) return null;
    return html` <section class="content-wrapper">
      <outline-container ?x-padding="${!this.removeXPadding}"><slot name="content"></slot></outline-container>
    </section>`;
  }

  /**
   * Template partial for the CTA slot.
   * @param classes
   * @returns TemplateResult | null
   */
  ctaTemplate(): TemplateResult | null {
    if (!this.hasCtaSlot) return null;

    const ctaWrapperClasses = {
      'cta-wrapper': true,
      [`cta-top-margin--${this.ctaTopMargin}`]: this.ctaTopMargin !== undefined,
    };

    return html` <section class="${classMap(ctaWrapperClasses)}">
      <slot name="cta"></slot>
    </section>`;
  }
}
